import React from 'react'
import { DataProxy } from 'apollo-cache'
import { FetchResult } from 'apollo-link'
import { ApolloError } from 'apollo-client'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

// Utils
import { addLicenseToCache } from '../../utils/licenseRedemption'
import { castError, capitalizeFirst } from '@sketch/utils'

// Constants
import { routes, SKETCH_WEBSITE } from '@sketch/constants'

// Components
import { ModalInjectedProps } from 'containers/ModalManager'
import { Button, LinkButton, Form } from '@sketch/components'
import Modal from 'components/Modal'
import FormField from 'modules/workspace/components/FormField'

// Styles
import { Text, InputWrapper } from './LicenseRedemptionModal.styles'

// Types
import {
  useRequestLicenseRedemptionMutation,
  RequestLicenseRedemptionMutation,
} from '@sketch/gql-types'

interface LicenseRedemptionModalProps extends ModalInjectedProps {
  customerId: string
  workspaceId: string
}

type FormValues = {
  licenseKey: string
}

// FORM
// Form Initial Values
const LICENSE_INITIAL_VALUES = {
  licenseKey: '',
}

// Form Validation
const licenseSchema = Yup.object().shape({
  licenseKey: Yup.string()
    .required('Please enter a valid license key')
    .matches(/^SK3(-[0-9]{4}){5}$/, {
      message: 'Please enter a valid license key',
    }),
})

/*
 * LicenseRedemptionModal
 *
 * Renders the license redemption modal.
 * Can be found in the Redeem Sketch license Panel in the Billing Page
 *
 */
export const LicenseRedemptionModal: React.FC<LicenseRedemptionModalProps> = ({
  hideModal,
  customerId,
  workspaceId,
}) => {
  const handleUpdate = (
    cache: DataProxy,
    { data: response }: FetchResult<RequestLicenseRedemptionMutation>
  ) => {
    addLicenseToCache(
      cache,
      response?.requestLicenseRedemption.licenseRedemption!,
      { customerId, status: null }
    )
    addLicenseToCache(
      cache,
      response?.requestLicenseRedemption.licenseRedemption!,
      { customerId, status: 'PENDING' }
    )
  }

  const handleOnSubmit = async (
    { licenseKey }: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    try {
      await requestLicenseRedemptionMutation({
        variables: {
          input: {
            customerId,
            serial: licenseKey,
          },
        },
      })
    } catch (e) {
      const error = castError(e)
      const graphQLError = error as ApolloError
      const errorMessage =
        graphQLError.graphQLErrors.map(error => error.message).join('; ') ||
        error.message

      actions.setFieldError('licenseKey', capitalizeFirst(errorMessage))
    }

    actions.setSubmitting(false)
  }

  // MUTATION
  const [
    requestLicenseRedemptionMutation,
    { loading, data },
  ] = useRequestLicenseRedemptionMutation({
    onError: 'unsafe-throw-exception',
    update: handleUpdate,
  })

  const { licenseRedemption } = data?.requestLicenseRedemption || {}

  if (licenseRedemption) {
    const { ownerEmail } = licenseRedemption

    return (
      <Modal title="Confirm license owner" onCancel={hideModal}>
        <Modal.Body>
          <Text>
            We sent an email to <b>{ownerEmail}</b> to verify that the license
            should be redeemed. The owner of this email needs to have a Sketch
            account and be a member of this workspace to complete the
            verification.
          </Text>
          <Text>
            Please make sure anyone who’s using the license key is invited to
            your workspace so they can continue using Sketch after the license
            key has been redeemed.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <LinkButton
            to={routes.WORKSPACE_SETTINGS_PEOPLE.create({ workspaceId })}
            onClick={hideModal}
          >
            Manage Workspace
          </LinkButton>
          <Button onClick={hideModal} variant="primary">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal title="Enter License Key" onCancel={hideModal}>
      <Formik
        validationSchema={licenseSchema}
        onSubmit={handleOnSubmit}
        initialValues={LICENSE_INITIAL_VALUES}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Modal.Body>
                <Text>
                  {' '}
                  Enter your license key to exchange it for credit. You&apos;ll
                  get $9 of credit per Seat on your account, multiplied by the
                  number of months remaining on your license.
                </Text>
                <Text>
                  <strong>Important</strong>: After you&apos;ve created a
                  Workspace and accepted the exchange email, the license will be{' '}
                  <strong>deactivated</strong>. This means{' '}
                  <strong>
                    anybody using Sketch with that license will lose access to
                    the Mac app, unless you invited them to your Workspace
                  </strong>
                  .
                </Text>
                <Text>
                  This action cannot be undone. If you&apos;re unsure, you can
                  always do this later from your Workspace Settings.
                </Text>
                <InputWrapper>
                  <FormField
                    name="licenseKey"
                    label="Your Sketch license key"
                    disabled={loading || isSubmitting}
                    placeholder="SK3-XXXX-XXXX-XXXX-XXXX-XXXX"
                  />
                </InputWrapper>
                <a
                  href={`${SKETCH_WEBSITE}/support/license-manager/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Can’t find the email with your key?
                </a>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={hideModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || isSubmitting}
                  loading={loading || isSubmitting}
                >
                  Send Request
                </Button>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
